
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { assertIsFn, AddContext } from "@storybook/addon-docs";

import { ArgsTable, Canvas, Meta, Story } from '@storybook/addon-docs';
import { makeTwigEmbedIfHtml } from '../../make-twig-include.js';
import demo from './demo/demo.twig';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Meta title="Components/Heading" argTypes={{
      content: {
        type: {
          name: 'string',
          required: true
        },
        description: 'The content of the heading. Also available as a block.'
      },
      id: {
        type: 'string',
        description: 'Used to identify the heading element for permalinks.'
      },
      level: {
        type: 'number',
        description: 'Determines the visual size and/or default `tag_name` of the heading.',
        control: {
          type: 'range',
          min: -2,
          max: 6,
          step: 1
        }
      },
      permalink: {
        type: 'boolean',
        description: 'Include an iconographic permalink handle with the heading.'
      },
      subheading: {
        type: 'string',
        description: 'Content to follow the main heading. Also available as a block.'
      },
      tag_name: {
        type: 'string',
        description: 'The semantic element to use for the heading, usually an `h*` element. If abscent, the `level` will be used to determine the best element.',
        options: ['', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
        control: {
          type: 'select'
        }
      },
      weight: {
        type: 'string',
        description: 'The desired `font-weight` option for the heading.',
        options: ['', 'light'],
        control: {
          type: 'select'
        }
      },
      class: {
        type: 'string',
        description: 'If necessary, additional classes can be added to headings.'
      }
    }} parameters={{
      docs: {
        transformSource: (_src, storyContext) => makeTwigEmbedIfHtml('@cloudfour/components/heading/heading.twig', storyContext.args || storyContext.initialArgs || {}, ['content', 'subheading'])
      }
    }} mdxType="Meta" />
    <h1>{`Heading`}</h1>
    <p>{`It's important for accessibility and for search engines that page sections include helpful heading elements in a logical, descending order (`}<a parentName="p" {...{
        "href": "https://dequeuniversity.com/assets/html/jquery-summit/html5/slides/headings.html"
      }}>{`more info`}</a>{`). But there are times when `}<a parentName="p" {...{
        "href": "/docs/design-defaults--heading-level-1#headings"
      }}>{`our default `}<inlineCode parentName="a">{`<h1>`}</inlineCode>{` through `}<inlineCode parentName="a">{`<h6>`}</inlineCode>{` styles`}</a>{` may not be enough:`}</p>
    <ul>
      <li parentName="ul">{`The most appropriate heading element may look too small or too large visually.`}</li>
      <li parentName="ul">{`A unique situation may call for a heading with an alternate visual style (such as a lighter weight).`}</li>
      <li parentName="ul">{`Longer pages may benefit from additional features added to headings, such as permalinks or subheadings.`}</li>
    </ul>
    <p>{`The Heading component addresses these scenarios by applying heading styles for the desired heading level, with a few bonus features not possible via heading elements alone.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Example" args={{
        content: 'Hello world',
        level: 1
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
    </Canvas>
    <ArgsTable story="Example" mdxType="ArgsTable" />
    <p>{`Note that the `}<inlineCode parentName="p">{`content`}</inlineCode>{` and `}<inlineCode parentName="p">{`subheading`}</inlineCode>{` properties are also available as `}<a parentName="p" {...{
        "href": "https://twig.symfony.com/doc/3.x/tags/block.html"
      }}>{`blocks`}</a>{`, allowing you to include HTML or template logic where desired.`}</p>
    <h2>{`Levels`}</h2>
    <p>{`The size of the heading is set by the `}<inlineCode parentName="p">{`level`}</inlineCode>{` property. A `}<inlineCode parentName="p">{`level`}</inlineCode>{` of `}<inlineCode parentName="p">{`1`}</inlineCode>{` will be equal in size to a default `}<inlineCode parentName="p">{`h1`}</inlineCode>{`, a `}<inlineCode parentName="p">{`level`}</inlineCode>{` of `}<inlineCode parentName="p">{`2`}</inlineCode>{` to a default `}<inlineCode parentName="p">{`h2`}</inlineCode>{`, and so on. In addition to the six heading levels present in HTML, there are larger heading sizes available at even lower levels. These can help distinguish page-level banners and titles from the primary page content.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Level`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`HTML Heading`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Modifier Class`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`-2`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`–`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`c-heading--level-n2`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`-1`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`–`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`c-heading--level-n1`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`0`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`–`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`c-heading--level-0`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`1`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<h1>`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`c-heading--level-1`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`2`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<h2>`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`c-heading--level-2`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`3`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<h3>`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`c-heading--level-3`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`4`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<h4>`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`c-heading--level-4`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`5`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<h5>`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`c-heading--level-5`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`6`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`<h6>`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`c-heading--level-6`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <Canvas isColumn mdxType="Canvas">
  <Story name="Level Minus 2" args={{
        level: -2,
        content: 'Level -2'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
  <Story name="Level Minus 1" args={{
        level: -1,
        content: 'Level -1'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
  <Story name="Level 0" args={{
        level: 0,
        content: 'Level 0'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
  <Story name="Level 1" args={{
        level: 1,
        content: 'Level 1'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
  <Story name="Level 2" args={{
        level: 2,
        content: 'Level 2'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
  <Story name="Level 3" args={{
        level: 3,
        content: 'Level 3'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
  <Story name="Level 4" args={{
        level: 4,
        content: 'Level 4'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
  <Story name="Level 5" args={{
        level: 5,
        content: 'Level 5'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
  <Story name="Level 6" args={{
        level: 6,
        content: 'Level 6'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
    </Canvas>
    <h2>{`Weights`}</h2>
    <p>{`By default, headings use the value of our `}<inlineCode parentName="p">{`font-weight.$bold`}</inlineCode>{` token so they'll stand out from adjacent content. In some situations, a lighter weight may be helpful to differentiate larger headings from one another or to emphasize specific key words denoted via the `}<inlineCode parentName="p">{`b`}</inlineCode>{` or `}<inlineCode parentName="p">{`strong`}</inlineCode>{` elements.`}</p>
    <Canvas isColumn mdxType="Canvas">
  <Story name="Default weight" args={{
        level: 1,
        content: 'Default weight'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
  <Story name="Medium weight" args={{
        level: 1,
        weight: 'medium',
        content: 'Medium weight with <b>bold</b> word'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
  <Story name="Light weight" args={{
        level: 1,
        weight: 'light',
        content: 'Light weight with <b>bold</b> word'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
    </Canvas>
    <h2>{`Permalinks`}</h2>
    <p>{`When the `}<inlineCode parentName="p">{`permalink`}</inlineCode>{` property is `}<inlineCode parentName="p">{`true`}</inlineCode>{`, we alter the markup of the heading based on `}<a parentName="p" {...{
        "href": "http://codepen.io/marcysutton/pen/rLKvgZ"
      }}>{`a technique from Marcy Sutton `}</a>{` that involves three elements:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`c-heading`}</inlineCode>{`: A container, ideally without any semantics (`}<inlineCode parentName="li">{`<div>`}</inlineCode>{`).`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`c-heading__content`}</inlineCode>{`: The actual semantic heading element (usually `}<inlineCode parentName="li">{`<h1>`}</inlineCode>{` – `}<inlineCode parentName="li">{`<h6>`}</inlineCode>{`), including the `}<inlineCode parentName="li">{`id`}</inlineCode>{` referenced by `}<inlineCode parentName="li">{`c-heading__permalink`}</inlineCode>{`.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`c-heading__permalink`}</inlineCode>{`: The `}<inlineCode parentName="li">{`<a>`}</inlineCode>{` element that exposes the permalink to the user. This element is kept separate from the actual heading so it won't disrupt a user's ability to navigate via headings.`}</li>
    </ul>
    <p>{`You can specify an ID that will be used for the heading (and therefore the permalink hash) using the `}<inlineCode parentName="p">{`id`}</inlineCode>{` property. (The `}<inlineCode parentName="p">{`id`}</inlineCode>{` property can also be used without permalinking.)`}</p>
    <p>{`Note that the widescreen layout of this pattern is optimized for `}<a parentName="p" {...{
        "href": "/docs/objects-container--prose#prose"
      }}>{`our prose containers`}</a>{` with generous horizontal whitespace.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Permalink" args={{
        content: 'Hello world',
        level: 1,
        permalink: true,
        id: 'permalink-example'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
    </Canvas>
    <h2>{`Subheadings`}</h2>
    <p>{`The context of some headings may be reenforced by some adjacent content. In those cases, we provide a `}<inlineCode parentName="p">{`subheading`}</inlineCode>{` property (or block) for including this content. At larger sizes, it will attempt to display itself inline as the content allows.`}</p>
    <p>{`This option does not use `}<inlineCode parentName="p">{`hgroup`}</inlineCode>{` due to `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Element/hgroup#usage_notes"
      }}>{`its absence from any shipping outline algorithms`}</a>{`. Instead, a humble `}<inlineCode parentName="p">{`p`}</inlineCode>{` element is used as described `}<a parentName="p" {...{
        "href": "https://www.w3.org/TR/html52/common-idioms-without-dedicated-elements.html#common-idioms-without-dedicated-elements"
      }}>{`in this W3C "common idiom"`}</a>{`.`}</p>
    <Canvas mdxType="Canvas">
  <Story name="Subheading" args={{
        level: 1,
        content: 'Watch',
        subheading: 'Smashing Conference (<time datetime="2018-10-23">Oct 23, 2018</time>)'
      }} mdxType="Story">
    {args => demo(args)}
  </Story>
    </Canvas>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

export const example = (args => demo(args));
example.storyName = 'Example';
example.args = {
  content: 'Hello world',
  level: 1
};
example.parameters = { storySource: { source: 'args => demo(args)' } };

export const levelMinus2 = (args => demo(args));
levelMinus2.storyName = 'Level Minus 2';
levelMinus2.args = {
  level: -2,
  content: 'Level -2'
};
levelMinus2.parameters = { storySource: { source: 'args => demo(args)' } };

export const levelMinus1 = (args => demo(args));
levelMinus1.storyName = 'Level Minus 1';
levelMinus1.args = {
  level: -1,
  content: 'Level -1'
};
levelMinus1.parameters = { storySource: { source: 'args => demo(args)' } };

export const level0 = (args => demo(args));
level0.storyName = 'Level 0';
level0.args = {
  level: 0,
  content: 'Level 0'
};
level0.parameters = { storySource: { source: 'args => demo(args)' } };

export const level1 = (args => demo(args));
level1.storyName = 'Level 1';
level1.args = {
  level: 1,
  content: 'Level 1'
};
level1.parameters = { storySource: { source: 'args => demo(args)' } };

export const level2 = (args => demo(args));
level2.storyName = 'Level 2';
level2.args = {
  level: 2,
  content: 'Level 2'
};
level2.parameters = { storySource: { source: 'args => demo(args)' } };

export const level3 = (args => demo(args));
level3.storyName = 'Level 3';
level3.args = {
  level: 3,
  content: 'Level 3'
};
level3.parameters = { storySource: { source: 'args => demo(args)' } };

export const level4 = (args => demo(args));
level4.storyName = 'Level 4';
level4.args = {
  level: 4,
  content: 'Level 4'
};
level4.parameters = { storySource: { source: 'args => demo(args)' } };

export const level5 = (args => demo(args));
level5.storyName = 'Level 5';
level5.args = {
  level: 5,
  content: 'Level 5'
};
level5.parameters = { storySource: { source: 'args => demo(args)' } };

export const level6 = (args => demo(args));
level6.storyName = 'Level 6';
level6.args = {
  level: 6,
  content: 'Level 6'
};
level6.parameters = { storySource: { source: 'args => demo(args)' } };

export const defaultWeight = (args => demo(args));
defaultWeight.storyName = 'Default weight';
defaultWeight.args = {
  level: 1,
  content: 'Default weight'
};
defaultWeight.parameters = { storySource: { source: 'args => demo(args)' } };

export const mediumWeight = (args => demo(args));
mediumWeight.storyName = 'Medium weight';
mediumWeight.args = {
  level: 1,
  weight: 'medium',
  content: 'Medium weight with <b>bold</b> word'
};
mediumWeight.parameters = { storySource: { source: 'args => demo(args)' } };

export const lightWeight = (args => demo(args));
lightWeight.storyName = 'Light weight';
lightWeight.args = {
  level: 1,
  weight: 'light',
  content: 'Light weight with <b>bold</b> word'
};
lightWeight.parameters = { storySource: { source: 'args => demo(args)' } };

export const permalink = (args => demo(args));
permalink.storyName = 'Permalink';
permalink.args = {
  content: 'Hello world',
  level: 1,
  permalink: true,
  id: 'permalink-example'
};
permalink.parameters = { storySource: { source: 'args => demo(args)' } };

export const subheading = (args => demo(args));
subheading.storyName = 'Subheading';
subheading.args = {
  level: 1,
  content: 'Watch',
  subheading: 'Smashing Conference (<time datetime="2018-10-23">Oct 23, 2018</time>)'
};
subheading.parameters = { storySource: { source: 'args => demo(args)' } };

const componentMeta = { title: 'Components/Heading', parameters: {
  docs: {
    transformSource: (_src, storyContext) => makeTwigEmbedIfHtml('@cloudfour/components/heading/heading.twig', storyContext.args || storyContext.initialArgs || {}, ['content', 'subheading'])
  }
}, argTypes: {
  content: {
    type: {
      name: 'string',
      required: true
    },
    description: 'The content of the heading. Also available as a block.'
  },
  id: {
    type: 'string',
    description: 'Used to identify the heading element for permalinks.'
  },
  level: {
    type: 'number',
    description: 'Determines the visual size and/or default `tag_name` of the heading.',
    control: {
      type: 'range',
      min: -2,
      max: 6,
      step: 1
    }
  },
  permalink: {
    type: 'boolean',
    description: 'Include an iconographic permalink handle with the heading.'
  },
  subheading: {
    type: 'string',
    description: 'Content to follow the main heading. Also available as a block.'
  },
  tag_name: {
    type: 'string',
    description: 'The semantic element to use for the heading, usually an `h*` element. If abscent, the `level` will be used to determine the best element.',
    options: ['', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    control: {
      type: 'select'
    }
  },
  weight: {
    type: 'string',
    description: 'The desired `font-weight` option for the heading.',
    options: ['', 'light'],
    control: {
      type: 'select'
    }
  },
  class: {
    type: 'string',
    description: 'If necessary, additional classes can be added to headings.'
  }
}, includeStories: ["example","levelMinus2","levelMinus1","level0","level1","level2","level3","level4","level5","level6","defaultWeight","mediumWeight","lightWeight","permalink","subheading"],  };

const mdxStoryNameToKey = {"Example":"example","Level Minus 2":"levelMinus2","Level Minus 1":"levelMinus1","Level 0":"level0","Level 1":"level1","Level 2":"level2","Level 3":"level3","Level 4":"level4","Level 5":"level5","Level 6":"level6","Default weight":"defaultWeight","Medium weight":"mediumWeight","Light weight":"lightWeight","Permalink":"permalink","Subheading":"subheading"};

componentMeta.parameters = componentMeta.parameters || {};
componentMeta.parameters.docs = {
  ...(componentMeta.parameters.docs || {}),
  page: () => <AddContext mdxStoryNameToKey={mdxStoryNameToKey} mdxComponentAnnotations={componentMeta}><MDXContent /></AddContext>,
};

export default componentMeta;